import { Registration } from '../models/registration';
import { ExtendedSearchReqResponse } from './search-service';

// The plato search (www, staging) returns every plate in each Current/Prefix
// pattern group, unsorted. DEV2's Mode 2 gateway shapes the same groups; this
// applies its rules (plato-search-dev2-mode2 extended_search.py) so release
// hosts show the same groups:
// - the broad letters-only group ('????HUG', '??HUG') keeps only plato's
//   preferred prefixes (preferred_prefix in helpers/cached.py, same on prod);
// - for sale first, then DVLA, then registry entries (id order within each),
//   capped at 100 per group. DEV2 caps by id before ranking, but its id order
//   puts listed plates early; ranking the whole group shows the same plates
//   first here (HU51 GHS, HU16 GHS for HUGH).
// `count` is the group total after the prefix filter, as on DEV2.

const GROUP_LIMIT = 100;

const PREFERRED_PREFIXES: string[] = ['A', 'AM6', 'B', 'B16', 'BE6', 'BE7', 'BO55', 'BU51', 'C', 'CA7', 'CO51', 'DO6', 'DR', 'EA51', 'EA7', 'F1', 'F17', 'FA57', 'FA7', 'H15', 'H8', 'HE12', 'HO57', 'JE7', 'JU57', 'K', 'LA57', 'LO57', 'ME', 'ME51', 'ME55', 'MR', 'MR5', 'MU55', 'MU57', 'MY', 'NO51', 'O', 'OK', 'OO', 'PE7', 'SS5', 'U', 'UR', 'US', 'V12', 'V6', 'V8', 'VE7', 'WE', 'WE7', 'X', 'XX', 'Y'];

const PREFERRED = PREFERRED_PREFIXES.map((p) => {
  const m = /^([A-Z]+)(\d*)$/.exec(p);
  return { letters: m[1], digits: m[2] };
});

// prefix letters and digits of a current (AB12CDE) or prefix (A123BCD) plate
function parts(reg: string): { prefix: string; numbers: string } | null {
  const r = (reg || '').replace(/\s+/g, '').toUpperCase();
  const m = /^([A-Z]{2})(\d{2})[A-Z]{3}$/.exec(r) || /^([A-Z])(\d{1,3})[A-Z]{3}$/.exec(r);
  return m ? { prefix: m[1], numbers: m[2] } : null;
}

function preferred(reg: string): boolean {
  const p = parts(reg);
  return !!p && PREFERRED.some((e) => e.letters === p.prefix && p.numbers.startsWith(e.digits));
}

// letters-only groups: current '%%##HUG', prefix '%?HUG'
function isCatchAll(g: ExtendedSearchReqResponse): boolean {
  const v = g.int_version || '';
  return v.startsWith('%%##') || v.startsWith('%?');
}

function rank(r: Registration): number {
  const owner = r.plate_owner ?? r.plateOwner;
  if (owner !== 0 && owner !== -1 && owner != null && r.price > 0) return 0;
  if (owner === 0) return 1;
  return 2;
}

export function shapeLikeDev2(groups: ExtendedSearchReqResponse[]): ExtendedSearchReqResponse[] {
  if (!Array.isArray(groups)) return groups;
  return groups.map((g) => {
    let rows = (g.results || []).slice();
    if (isCatchAll(g)) rows = rows.filter((r) => preferred(r.registration));
    rows.sort((a, b) => rank(a) - rank(b) || (a.id || 0) - (b.id || 0));
    return {
      ...g,
      count: rows.length,
      hasMore: rows.length > GROUP_LIMIT,
      results: rows.slice(0, GROUP_LIMIT),
    };
  });
}
